<div class="xr-page conversations">
  <header class="xr-page-header">
    <div>
      <h1 class="xr-page-title">{{ 'conversations.title' | translate }}</h1>
    </div>
    <div class="conversations-toolbar">
      <app-company-filter></app-company-filter>
      <label class="xr-search conversations-search">
        <i-tabler name="search" class="icon-18" aria-hidden="true"></i-tabler>
        <span class="xr-sr-only">{{ 'conversations.search' | translate }}</span>
        <input type="search" [placeholder]="'conversations.search' | translate"
               [value]="search" (input)="onSearch($any($event.target).value)">
      </label>
    </div>
  </header>

  @if (!loading && !failed) {
    <div class="conversations-filters">
      <div class="xr-filters" role="group" [attr.aria-label]="'conversations.period_label' | translate">
        @for (item of periods; track item) {
          <button type="button" class="xr-filter" [class.active]="period === item" [attr.aria-pressed]="period === item"
                  (click)="setPeriod(item)">
            {{ 'conversations.period_' + item | translate }}
          </button>
        }
      </div>
      @if (technicianOptions.length > 1 || technicianId !== null) {
        <label class="conversations-technician">
          <span class="xr-sr-only">{{ 'conversations.technician_filter' | translate }}</span>
          <select [ngModel]="technicianId" (ngModelChange)="setTechnician($event)"
                  [attr.aria-label]="'conversations.technician_filter' | translate">
            <option [ngValue]="null">{{ 'conversations.all_technicians' | translate }}</option>
            @for (technician of technicianOptions; track technician.id) {
              <option [ngValue]="technician.id">{{ technician.name }}</option>
            }
          </select>
        </label>
      }
    </div>
  }

  @if (loading) {
    <div class="conversations-loading">
      <mat-spinner diameter="36"></mat-spinner>
    </div>
  } @else if (failed) {
    <div class="xr-empty">
      <p>{{ 'conversations.load_error' | translate }}</p>
      <button type="button" class="xr-btn xr-btn--secondary" (click)="load()">{{ 'conversations.retry' | translate }}</button>
    </div>
  } @else if (!days.length) {
    <div class="xr-empty">
      {{ (filtersActive ? 'conversations.no_results' : 'conversations.empty') | translate }}
    </div>
  } @else {
    <div class="conversations-grid">
      <section class="xr-card conversations-days" [attr.aria-label]="'conversations.days' | translate">
        <h2 class="conversations-caption">{{ 'conversations.days' | translate }}</h2>
        <!-- On a phone the days are a menu: the row of folders scrolled sideways and showed only the first one -->
        <label class="conversations-day-select">
          <span class="xr-sr-only">{{ 'conversations.days' | translate }}</span>
          <select [value]="selectedDay" (change)="pickDay($any($event.target).value)">
            @for (entry of days; track entry.day) {
              <option [value]="entry.day" [selected]="entry.day === selectedDay">
                {{ dayLabel(entry.day) }} ({{ entry.calls.length }})
              </option>
            }
          </select>
        </label>
        <div class="conversations-day-list">
          @for (entry of days; track entry.day) {
            <button type="button" class="conversations-day" [class.active]="entry.day === selectedDay"
                    [attr.aria-pressed]="entry.day === selectedDay" (click)="pickDay(entry.day)">
              <i-tabler name="folder-filled" class="icon-20 conversations-day-icon" aria-hidden="true"></i-tabler>
              <span class="conversations-day-text">
                <strong>{{ dayLabel(entry.day) }}</strong>
                <small>{{ dayLong(entry.day) }}</small>
              </span>
              <span class="conversations-count">{{ entry.calls.length }}</span>
            </button>
          }
        </div>
      </section>

      <section class="xr-card conversations-calls" [attr.aria-label]="'conversations.calls_of_day' | translate">
        <div class="conversations-calls-head">
          <h2 class="xr-card-title">{{ selectedDay ? dayLong(selectedDay) : '' }}</h2>
          <span>{{ currentCalls.length }} {{ (currentCalls.length === 1 ? 'conversations.call' : 'conversations.calls') | translate }}</span>
        </div>
        @for (call of currentCalls; track call.uuid) {
          <button type="button" class="conversations-call" [class.active]="call.uuid === selected?.uuid"
                  [attr.aria-pressed]="call.uuid === selected?.uuid" (click)="open(call)">
            <span class="conversations-call-top">
              <strong>{{ call.room_name }}</strong>
              <span>{{ timeRange(call) }}</span>
            </span>
            <span class="conversations-call-meta">
              {{ call.technician_name || ('conversations.technician' | translate) }} · {{ minutes(call) }} min
            </span>
            <span class="conversations-chips">
              @if (call.languages.length) {
                <span class="xr-badge xr-badge--plain">{{ languages(call) }}</span>
              }
              <span class="xr-badge xr-badge--plain">{{ call.message_count }} {{ 'conversations.phrases' | translate }}</span>
              <span class="xr-badge xr-badge--plain" [ngClass]="statusClass(call)">{{ statusKey(call) | translate }}</span>
            </span>
          </button>
        }
      </section>

      <section #reader class="xr-card conversations-reader" [attr.aria-label]="'conversations.reader' | translate">
        @if (selected) {
          <div class="conversations-reader-head">
            <div>
              <h2 class="xr-card-title">{{ selected.room_name }}</h2>
              <p>
                {{ dayLong(selected.day) }} · {{ timeRange(selected) }}
                @if (selected.technician_name) {
                  · {{ 'conversations.technician' | translate }}: {{ selected.technician_name }}
                }
              </p>
            </div>
            @if (selected.document_uuid) {
              <button type="button" class="xr-btn xr-btn--primary" (click)="downloadPdf()" [disabled]="downloading">
                <i-tabler name="download" class="icon-18" aria-hidden="true"></i-tabler>
                {{ 'conversations.download_pdf' | translate }}
              </button>
            }
          </div>
          <div class="conversations-legend">
            <span><span class="legend-swatch legend-swatch--me"></span>{{ 'conversations.operator' | translate }}</span>
            <span><span class="legend-swatch legend-swatch--tech"></span>{{ 'conversations.field_technician' | translate }}</span>
            <span class="legend-hint">{{ 'conversations.original_hint' | translate }}</span>
          </div>
          <div class="conversations-messages">
            @if (detailLoading) {
              <div class="conversations-loading"><mat-spinner diameter="28"></mat-spinner></div>
            }
            @if (detailFailed) {
              <p class="xr-empty">{{ 'conversations.detail_error' | translate }}</p>
            }
            @for (message of detail?.messages ?? []; track $index) {
              <div class="conversations-message" [class.mine]="message.is_local">
                <span class="conversations-message-who">
                  {{ message.is_local ? (selected.operator_name || ('conversations.operator' | translate)) : (selected.technician_name || ('conversations.technician' | translate)) }}
                  · {{ time(message.captured_at) }}
                  @if (message.kind === 'chat') {
                    · {{ 'conversations.typed' | translate }}
                  }
                </span>
                <div class="conversations-bubble">
                  <span class="conversations-main">{{ message.translation || message.text }}</span>
                  @if (message.translation) {
                    <span class="conversations-original">{{ message.text }}</span>
                  }
                </div>
              </div>
            }
          </div>
        }
      </section>
    </div>
  }
</div>
